import { useMemo, useState } from "react";

import { Button } from "../../shared/ui/button.js";
import { ResourceState } from "../../shared/ui/resource-state.js";
import { DecisionTree } from "./bot-decision-tree.js";
import {
  decisionGraphActions,
  decisionGraphView,
  parseDecisionGraphParameters,
  type DecisionGraphNodeDraft,
  type DecisionGraphParametersDraft,
} from "./decision-tree-models.js";

const defaultCondition = { kind: "position", operator: "is", value: "flat" } as const;

function nextId(nodes: readonly DecisionGraphNodeDraft[], prefix: string): string {
  const ids = new Set(nodes.map((node) => node.id));
  for (let number = 1; ; number++) {
    const candidate = `${prefix}-${number}`;
    if (!ids.has(candidate)) return candidate;
  }
}

function replaceNode(
  parameters: DecisionGraphParametersDraft,
  nodeId: string,
  replacement: DecisionGraphNodeDraft,
): DecisionGraphParametersDraft {
  return {
    ...parameters,
    nodes: parameters.nodes.map((node) => (node.id === nodeId ? replacement : node)),
  };
}

function actionWithType(
  node: Extract<DecisionGraphNodeDraft, { readonly kind: "action" }>,
  action: (typeof decisionGraphActions)[number],
): DecisionGraphNodeDraft {
  const common = {
    action,
    id: node.id,
    kind: "action" as const,
    ...(node.label === undefined ? {} : { label: node.label }),
    ...(node.stop === undefined ? {} : { stop: node.stop }),
    ...(node.target === undefined ? {} : { target: node.target }),
  };
  return action.startsWith("reduce-")
    ? { ...common, closeFraction: node.closeFraction ?? 0.5 }
    : common;
}

function ConditionEditor({
  node,
  onChange,
}: {
  readonly node: Extract<DecisionGraphNodeDraft, { readonly kind: "decision" }>;
  readonly onChange: (node: DecisionGraphNodeDraft) => void;
}) {
  const [value, setValue] = useState(() => JSON.stringify(node.condition, null, 2));
  const [problem, setProblem] = useState("");
  return (
    <div className="field-stack decision-node-field--wide">
      <label htmlFor={`condition-${node.id}`}>Typed condition</label>
      <textarea
        id={`condition-${node.id}`}
        onChange={(event) => {
          const next = event.currentTarget.value;
          setValue(next);
          try {
            const condition: unknown = JSON.parse(next);
            setProblem("");
            onChange({ ...node, condition });
          } catch {
            setProblem("Condition must be valid JSON before this revision can be saved.");
          }
        }}
        rows={12}
        spellCheck={false}
        value={value}
      />
      {problem === "" ? null : <span className="field-error">{problem}</span>}
      <span className="field-hint">
        TradingEngine validates every field reference, operator, branch, and cycle on save.
      </span>
    </div>
  );
}

function OptionalExpressionEditor({
  field,
  label,
  node,
  onChange,
}: {
  readonly field: "stop" | "target";
  readonly label: string;
  readonly node: Extract<DecisionGraphNodeDraft, { readonly kind: "action" }>;
  readonly onChange: (node: DecisionGraphNodeDraft) => void;
}) {
  const [value, setValue] = useState(() =>
    node[field] === undefined ? "" : JSON.stringify(node[field], null, 2),
  );
  const [problem, setProblem] = useState("");
  return (
    <div className="field-stack decision-node-field--wide">
      <label htmlFor={`${field}-${node.id}`}>{label}</label>
      <textarea
        id={`${field}-${node.id}`}
        onChange={(event) => {
          const next = event.currentTarget.value;
          setValue(next);
          if (next.trim() === "") {
            setProblem("");
            onChange({ ...node, [field]: undefined });
            return;
          }
          try {
            const expression: unknown = JSON.parse(next);
            setProblem("");
            onChange({ ...node, [field]: expression });
          } catch {
            setProblem(`${label} must be valid expression JSON.`);
          }
        }}
        placeholder='{"kind":"constant","value":100}'
        rows={5}
        spellCheck={false}
        value={value}
      />
      {problem === "" ? null : <span className="field-error">{problem}</span>}
    </div>
  );
}

export function DecisionGraphEditor({
  botName,
  botVersion,
  onChange,
  orderType,
  parameters: rawParameters,
  revision,
  symbol,
}: {
  readonly botName: string;
  readonly botVersion: number;
  readonly onChange: (parameters: DecisionGraphParametersDraft) => void;
  readonly orderType: string;
  readonly parameters: unknown;
  readonly revision: number;
  readonly symbol: string;
}) {
  const parameters = useMemo(() => parseDecisionGraphParameters(rawParameters), [rawParameters]);
  const [selectedNodeId, setSelectedNodeId] = useState<string>();

  if (parameters === null) {
    return (
      <ResourceState
        kind="degraded"
        title="Decision graph cannot be visualized"
        description="Repair the advanced strategy JSON, then the visual editor will become available."
      />
    );
  }

  const effectiveSelectedNodeId = parameters.nodes.some((node) => node.id === selectedNodeId)
    ? selectedNodeId
    : parameters.entryNodeId;
  const selectedNode = parameters.nodes.find((node) => node.id === effectiveSelectedNodeId);
  const monthly = parameters.calendar?.kind === "month-end";
  const view = decisionGraphView(parameters, {
    botVersion,
    name: botName,
    orderType,
    revision,
    symbol,
  });
  const updateSelected = (node: DecisionGraphNodeDraft): void => {
    if (effectiveSelectedNodeId === undefined) return;
    onChange(replaceNode(parameters, effectiveSelectedNodeId, node));
  };
  const referenced =
    selectedNode === undefined ||
    parameters.entryNodeId === selectedNode.id ||
    parameters.nodes.some(
      (node) =>
        node.kind === "decision" &&
        (node.onTrue === selectedNode.id || node.onFalse === selectedNode.id),
    );
  const addNode = (kind: "action" | "decision"): void => {
    const id = nextId(parameters.nodes, kind);
    const node: DecisionGraphNodeDraft =
      kind === "action"
        ? { action: "hold", id, kind, label: "Hold" }
        : {
            condition:
              parameters.calendar?.kind === "month-end"
                ? { kind: "monthly-history-ready" }
                : defaultCondition,
            id,
            kind,
            label: "New decision",
            onFalse: parameters.entryNodeId,
            onTrue: parameters.entryNodeId,
          };
    onChange({ ...parameters, nodes: [...parameters.nodes, node] });
    setSelectedNodeId(id);
  };

  return (
    <div className="decision-graph-editor section-stack">
      <div className="connection-heading">
        <div>
          <p className="eyebrow">Executable source of truth</p>
          <h3>Visual decision-graph editor</h3>
          <p className="section-description">
            Select a node to edit it. This exact graph becomes the next immutable revision.
          </p>
        </div>
        <div className="button-row">
          <Button onClick={() => addNode("decision")} tone="quiet" type="button">
            + Query
          </Button>
          <Button onClick={() => addNode("action")} tone="quiet" type="button">
            + Action
          </Button>
        </div>
      </div>
      <DecisionTree
        onSelectNode={setSelectedNodeId}
        {...(effectiveSelectedNodeId === undefined
          ? {}
          : { selectedNodeId: effectiveSelectedNodeId })}
        tree={view}
      />
      {selectedNode === undefined ? null : (
        <section className="decision-node-editor" aria-labelledby="selected-node-title">
          <div className="connection-heading">
            <div>
              <p className="eyebrow">Selected {selectedNode.kind}</p>
              <h3 id="selected-node-title">{selectedNode.id}</h3>
            </div>
            <div className="button-row">
              <Button
                disabled={parameters.entryNodeId === selectedNode.id}
                onClick={() => onChange({ ...parameters, entryNodeId: selectedNode.id })}
                tone="quiet"
                type="button"
              >
                Set as start
              </Button>
              <Button
                disabled={referenced || parameters.nodes.length === 1}
                onClick={() => {
                  onChange({
                    ...parameters,
                    nodes: parameters.nodes.filter((node) => node.id !== selectedNode.id),
                  });
                  setSelectedNodeId(parameters.entryNodeId);
                }}
                tone="quiet"
                title={referenced ? "Remove incoming branches or choose another start first." : ""}
                type="button"
              >
                Delete node
              </Button>
            </div>
          </div>
          <div className="field-grid">
            <div className="field-stack">
              <label htmlFor={`label-${selectedNode.id}`}>Display label</label>
              <input
                id={`label-${selectedNode.id}`}
                maxLength={120}
                onChange={(event) =>
                  updateSelected({ ...selectedNode, label: event.currentTarget.value })
                }
                value={selectedNode.label ?? ""}
              />
            </div>
            {selectedNode.kind === "action" ? (
              <>
                <div className="field-stack">
                  <label htmlFor={`action-${selectedNode.id}`}>Runtime action</label>
                  <select
                    id={`action-${selectedNode.id}`}
                    onChange={(event) => {
                      const action = event.currentTarget
                        .value as (typeof decisionGraphActions)[number];
                      updateSelected(actionWithType(selectedNode, action));
                    }}
                    value={selectedNode.action}
                  >
                    {decisionGraphActions
                      .filter(
                        (action) =>
                          !monthly || ["hold", "enter-long", "exit-long"].includes(action),
                      )
                      .map((action) => (
                        <option key={action} value={action}>
                          {action}
                        </option>
                      ))}
                  </select>
                </div>
                {selectedNode.action.startsWith("reduce-") ? (
                  <div className="field-stack">
                    <label htmlFor={`close-fraction-${selectedNode.id}`}>Close fraction</label>
                    <input
                      id={`close-fraction-${selectedNode.id}`}
                      max="1"
                      min="0.000001"
                      onChange={(event) =>
                        updateSelected({
                          ...selectedNode,
                          closeFraction: Number(event.currentTarget.value),
                        })
                      }
                      step="0.01"
                      type="number"
                      value={selectedNode.closeFraction ?? 0.5}
                    />
                  </div>
                ) : null}
                {monthly ? (
                  <p className="field-hint">
                    Actions request long, cash or retained exposure at the next session open. Stops,
                    targets and partial exits are not supported.
                  </p>
                ) : (
                  <>
                    <OptionalExpressionEditor
                      field="stop"
                      key={`${selectedNode.id}-stop`}
                      label="Stop expression (optional)"
                      node={selectedNode}
                      onChange={updateSelected}
                    />
                    <OptionalExpressionEditor
                      field="target"
                      key={`${selectedNode.id}-target`}
                      label="Target expression (optional)"
                      node={selectedNode}
                      onChange={updateSelected}
                    />
                  </>
                )}
              </>
            ) : (
              <>
                <div className="field-stack">
                  <label htmlFor={`true-${selectedNode.id}`}>Yes branch</label>
                  <select
                    id={`true-${selectedNode.id}`}
                    onChange={(event) =>
                      updateSelected({ ...selectedNode, onTrue: event.currentTarget.value })
                    }
                    value={selectedNode.onTrue}
                  >
                    {parameters.nodes.map((node) => (
                      <option key={node.id} value={node.id}>
                        {node.label ?? node.id}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="field-stack">
                  <label htmlFor={`false-${selectedNode.id}`}>No branch</label>
                  <select
                    id={`false-${selectedNode.id}`}
                    onChange={(event) =>
                      updateSelected({ ...selectedNode, onFalse: event.currentTarget.value })
                    }
                    value={selectedNode.onFalse}
                  >
                    {parameters.nodes.map((node) => (
                      <option key={node.id} value={node.id}>
                        {node.label ?? node.id}
                      </option>
                    ))}
                  </select>
                </div>
                <ConditionEditor
                  key={selectedNode.id}
                  node={selectedNode}
                  onChange={updateSelected}
                />
              </>
            )}
          </div>
        </section>
      )}
    </div>
  );
}
